import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';

import { FullPageLoader } from '@/components/FullPageLoader';
import { ErrorPage } from '@/components/layouts/ErrorPage';
import { LoginPage, SignupPage, SignupVerifyPage } from '@/modules/auth';

import type { ReactElement } from 'react';

// Lazy load heavy route components for better code splitting
const MapPage = lazy(() => import('@/modules/map').then((m) => ({ default: m.MapPage })));
const TablePage = lazy(() => import('@/modules/map').then((m) => ({ default: m.TablePage })));
const ReportsPage = lazy(() => import('@/modules/map').then((m) => ({ default: m.ReportsPage })));
const KnowledgeCenterPage = lazy(() =>
  import('@/pages/KnowledgeCenterPage').then((m) => ({ default: m.KnowledgeCenterPage }))
);
const ChangelogPage = lazy(() =>
  import('@/pages/ChangelogPage').then((m) => ({ default: m.ChangelogPage }))
);

export interface RouteConfig {
  path: string;
  element: ReactElement;
  isProtected?: boolean;
  title?: string;
}

export const routes: RouteConfig[] = [
  {
    path: '/login',
    element: <LoginPage />,
    isProtected: false,
    title: 'Login',
  },
  {
    path: '/signup',
    element: <SignupPage />,
    isProtected: false,
    title: 'Sign up',
  },
  {
    path: '/signup/verify',
    element: <SignupVerifyPage />,
    isProtected: false,
    title: 'Confirm signup',
  },
  {
    path: '/',
    element: <Navigate to="/map" replace />,
    isProtected: true,
  },
  {
    path: '/map',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <MapPage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Map',
  },
  {
    path: '/reports',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <ReportsPage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Reports',
  },
  {
    path: '/knowledge',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <KnowledgeCenterPage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Knowledge Center',
  },
  {
    path: '/changelog',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <ChangelogPage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Changelog',
  },
  {
    // MapPage, like `/sites` and `/updates` — the dashboard floats over the map.
    path: '/dashboard',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <MapPage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Dashboard',
  },
  {
    path: '/table',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <TablePage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Table',
  },
  {
    // Same element as `/map` on purpose: React keeps the one map mounted across
    // the switch, so moving between them never rebuilds it.
    path: '/sites',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <MapPage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Sites',
  },
  {
    path: '/updates',
    element: (
      <Suspense fallback={<FullPageLoader />}>
        <MapPage />
      </Suspense>
    ),
    isProtected: true,
    title: 'Updates',
  },
  {
    path: '*',
    element: <ErrorPage status="not-found" />,
    isProtected: false,
  },
];
